import { type FormEvent, useEffect, useState } from "react";
import {
  ApiError,
  api,
  type ContactoIn,
  type Proveedor,
  type ProveedorContacto,
} from "@/lib/api";
import ConfirmDialog from "@/ui/ConfirmDialog";
import { SkeletonRows } from "@/ui/Skeleton";
import Toggle from "@/ui/Toggle";
import { useToast } from "@/ui/Toast";

/**
 * Tab "Proveedores" de /configuracion. Solo admin (require_admin en
 * backend; tab oculto en navbar para no-admin).
 *
 * UX: lista de proveedores. Click expand → contactos editables inline.
 * "Es principal" es radio implicito — marcar uno desmarca a los demas
 * (lock automatico en backend).
 */
export default function ProveedoresPanel() {
  const toast = useToast();
  const [proveedores, setProveedores] = useState<Proveedor[]>([]);
  const [loading, setLoading] = useState(true);
  const [nombreNuevo, setNombreNuevo] = useState("");
  const [creando, setCreando] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [editNombre, setEditNombre] = useState("");
  const [confirmDel, setConfirmDel] = useState<Proveedor | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      const list = await api.get<Proveedor[]>("/api/proveedores");
      setProveedores(list);
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude cargar proveedores", msg);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  function toggleExpand(id: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function crearProveedor(e: FormEvent) {
    e.preventDefault();
    if (!nombreNuevo.trim()) return;
    setCreando(true);
    try {
      const p = await api.post<Proveedor>("/api/proveedores", {
        nombre: nombreNuevo.trim(),
      });
      toast.success("Proveedor creado", p.nombre);
      setNombreNuevo("");
      // Auto-expandir el recien creado para que el user vea el espacio
      // de contactos sin tener que click otro vez.
      setExpanded((prev) => new Set(prev).add(p.id));
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo crear", msg);
    } finally {
      setCreando(false);
    }
  }

  async function toggleActivo(p: Proveedor) {
    try {
      await api.patch(`/api/proveedores/${p.id}`, { activo: !p.activo });
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo actualizar", msg);
    }
  }

  function startEdit(p: Proveedor) {
    setEditandoId(p.id);
    setEditNombre(p.nombre);
  }

  async function saveEdit(p: Proveedor) {
    if (!editNombre.trim() || editNombre === p.nombre) {
      setEditandoId(null);
      return;
    }
    try {
      await api.patch(`/api/proveedores/${p.id}`, { nombre: editNombre.trim() });
      toast.success("Proveedor renombrado");
      setEditandoId(null);
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo renombrar", msg);
    }
  }

  async function eliminarProveedor(p: Proveedor) {
    try {
      await api.del(`/api/proveedores/${p.id}`);
      toast.success("Proveedor eliminado", p.nombre);
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo eliminar", msg);
    }
  }

  return (
    <>
      <form onSubmit={crearProveedor} className="card flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="flex-1">
          <label className="text-xs uppercase tracking-wide text-zinc-400">
            Nuevo proveedor
          </label>
          <input
            type="text"
            className="input mt-1"
            value={nombreNuevo}
            onChange={(e) => setNombreNuevo(e.target.value)}
            placeholder="ej. Queclink, JIMI, Concox, Suntech, Teltonika"
          />
        </div>
        <button
          type="submit"
          disabled={!nombreNuevo.trim() || creando}
          className="btn-primary disabled:opacity-50"
        >
          {creando ? "Creando..." : "+ Agregar"}
        </button>
      </form>

      <div className="card">
        {loading ? (
          <SkeletonRows rows={4} cols={3} />
        ) : proveedores.length === 0 ? (
          <p className="text-sm text-zinc-500">
            No hay proveedores todavía. Agrega el primero arriba.
          </p>
        ) : (
          <ul className="divide-y divide-ink-700">
            {proveedores.map((p) => {
              const isExpanded = expanded.has(p.id);
              return (
                <li key={p.id} className={p.activo ? "" : "opacity-50"}>
                  <div className="py-2 flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                    {editandoId === p.id ? (
                      <input
                        type="text"
                        autoFocus
                        className="input flex-1"
                        value={editNombre}
                        onChange={(e) => setEditNombre(e.target.value)}
                        onBlur={() => saveEdit(p)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveEdit(p);
                          if (e.key === "Escape") setEditandoId(null);
                        }}
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => toggleExpand(p.id)}
                        className="flex-1 text-left flex items-center gap-2 hover:text-white"
                      >
                        <span className="text-zinc-400 text-xs w-3">
                          {isExpanded ? "▼" : "▶"}
                        </span>
                        <span className="text-zinc-200">{p.nombre}</span>
                        <span className="text-xs text-zinc-500">
                          ({p.contactos.length} contacto{p.contactos.length === 1 ? "" : "s"})
                        </span>
                      </button>
                    )}
                    <div className="flex flex-wrap gap-1 lg:flex-shrink-0">
                      <button onClick={() => toggleActivo(p)} className="btn-ghost text-xs">
                        {p.activo ? "Desactivar" : "Activar"}
                      </button>
                      <button onClick={() => startEdit(p)} className="btn-ghost text-xs">
                        Renombrar
                      </button>
                      <button
                        onClick={() => setConfirmDel(p)}
                        className="btn-ghost text-xs text-red-400 hover:text-red-300"
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <ContactosSubpanel
                      proveedorId={p.id}
                      contactos={p.contactos}
                      onChanged={refresh}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {confirmDel && (
        <ConfirmDialog
          open={true}
          onOpenChange={(v) => !v && setConfirmDel(null)}
          title={`Eliminar "${confirmDel.nombre}"`}
          description="También se borrarán sus contactos. Las marcas asociadas pasan a quedar sin proveedor. Casos viejos no se modifican."
          variant="danger"
          confirmLabel="Eliminar"
          onConfirm={() => eliminarProveedor(confirmDel)}
        />
      )}
    </>
  );
}

// ============================================================================
// Sub-panel de contactos del proveedor (anidado)
// ============================================================================

function ContactosSubpanel({
  proveedorId,
  contactos,
  onChanged,
}: {
  proveedorId: number;
  contactos: ProveedorContacto[];
  onChanged: () => void | Promise<void>;
}) {
  const toast = useToast();
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmDel, setConfirmDel] = useState<ProveedorContacto | null>(null);

  // Form de nuevo contacto
  const [nombre, setNombre] = useState("");
  const [email, setEmail] = useState("");
  const [telefono, setTelefono] = useState("");
  const [esPrincipal, setEsPrincipal] = useState(false);

  function resetForm() {
    setNombre("");
    setEmail("");
    setTelefono("");
    setEsPrincipal(false);
    setShowForm(false);
  }

  async function crearContacto(e: FormEvent) {
    e.preventDefault();
    if (!nombre.trim() || !email.trim()) return;
    setSubmitting(true);
    try {
      const payload: ContactoIn = {
        nombre: nombre.trim(),
        email: email.trim(),
        telefono: telefono.trim() || null,
        es_principal: esPrincipal,
      };
      await api.post(`/api/proveedores/${proveedorId}/contactos`, payload);
      toast.success("Contacto agregado", email);
      resetForm();
      await onChanged();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo agregar", msg);
    } finally {
      setSubmitting(false);
    }
  }

  async function toggleEsPrincipal(c: ProveedorContacto) {
    try {
      await api.patch(`/api/proveedores/${proveedorId}/contactos/${c.id}`, {
        es_principal: !c.es_principal,
      });
      await onChanged();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo cambiar", msg);
    }
  }

  async function toggleActivo(c: ProveedorContacto) {
    try {
      await api.patch(`/api/proveedores/${proveedorId}/contactos/${c.id}`, {
        activo: !c.activo,
      });
      await onChanged();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo cambiar", msg);
    }
  }

  async function eliminar(c: ProveedorContacto) {
    try {
      await api.del(`/api/proveedores/${proveedorId}/contactos/${c.id}`);
      toast.success("Contacto eliminado");
      await onChanged();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo eliminar", msg);
    }
  }

  return (
    <div className="ml-5 mb-3 border-l-2 border-ink-700 pl-3 space-y-2">
      {contactos.length === 0 && !showForm ? (
        <p className="text-xs text-zinc-500 py-1">
          Sin contactos. Agrega uno para que la ventana "Enviar a proveedor" lo
          tome automáticamente.
        </p>
      ) : (
        <ul className="divide-y divide-ink-800">
          {contactos.map((c) => (
            <li
              key={c.id}
              className={`py-1.5 flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between ${c.activo ? "" : "opacity-50"}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-zinc-200">{c.nombre}</span>
                  {c.es_principal && (
                    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-accent-500/40 bg-accent-500/10 text-accent-300">
                      Principal
                    </span>
                  )}
                </div>
                <p className="text-xs text-zinc-500 truncate">
                  {c.email}
                  {c.telefono && ` · ${c.telefono}`}
                </p>
              </div>
              <div className="flex flex-wrap gap-1 lg:flex-shrink-0">
                <button onClick={() => toggleEsPrincipal(c)} className="btn-ghost text-xs">
                  {c.es_principal ? "Quitar principal" : "Hacer principal"}
                </button>
                <button onClick={() => toggleActivo(c)} className="btn-ghost text-xs">
                  {c.activo ? "Desactivar" : "Activar"}
                </button>
                <button
                  onClick={() => setConfirmDel(c)}
                  className="btn-ghost text-xs text-red-400 hover:text-red-300"
                >
                  Eliminar
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {showForm ? (
        <form onSubmit={crearContacto} className="space-y-2 bg-ink-900 rounded p-2 border border-ink-700">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <input
              type="text"
              autoFocus
              className="input"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Nombre del contacto"
              required
            />
            <input
              type="email"
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@proveedor.com"
              required
            />
            <input
              type="text"
              className="input"
              value={telefono}
              onChange={(e) => setTelefono(e.target.value)}
              placeholder="Teléfono (opcional)"
            />
            <Toggle
              checked={esPrincipal}
              onChange={setEsPrincipal}
              label="Hacer principal"
              className="text-sm"
            />
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={submitting || !nombre.trim() || !email.trim()}
              className="btn-primary text-xs disabled:opacity-50"
            >
              {submitting ? "Agregando..." : "+ Agregar contacto"}
            </button>
            <button type="button" onClick={resetForm} className="btn-ghost text-xs">
              Cancelar
            </button>
          </div>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setShowForm(true)}
          className="btn-ghost text-xs text-accent-400 hover:text-accent-300"
        >
          + Agregar contacto
        </button>
      )}

      {confirmDel && (
        <ConfirmDialog
          open={true}
          onOpenChange={(v) => !v && setConfirmDel(null)}
          title={`Eliminar contacto "${confirmDel.nombre}"`}
          description={`Email ${confirmDel.email}. No se puede deshacer.`}
          variant="danger"
          confirmLabel="Eliminar"
          onConfirm={() => eliminar(confirmDel)}
        />
      )}
    </div>
  );
}
